import { ref, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useTracking } from '../services/tracking';
import { TrackingEventType } from '@monorepo/types';

export function usePageTracking() {
	const route = useRoute();
	const tracking = useTracking();

	const pageStartTime = ref<number>(Date.now());
	const scrollDepth = ref<number>(0);
	const interactionCount = ref<number>(0);
	const maxScrollDepth = ref<number>(0);

	let scrollTimer: number | null = null;
	let engagementTimer: number | null = null;

	// Track scroll depth
	const trackScrollDepth = () => {
		const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
		const windowHeight = window.innerHeight;
		const documentHeight = document.documentElement.scrollHeight;

		const currentScrollDepth = Math.round(((scrollTop + windowHeight) / documentHeight) * 100);
		scrollDepth.value = Math.min(currentScrollDepth, 100);

		if (scrollDepth.value > maxScrollDepth.value) {
			maxScrollDepth.value = scrollDepth.value;
		}
	};

	// Debounced scroll tracking
	const handleScroll = () => {
		if (scrollTimer) clearTimeout(scrollTimer);
		scrollTimer = window.setTimeout(trackScrollDepth, 100);
	};

	// Track user interactions
	const trackInteraction = () => {
		interactionCount.value++;
	};

	// Send engagement data periodically
	const sendEngagementData = () => {
		const timeSpent = Date.now() - pageStartTime.value;

		tracking.trackEngagementEvent({
			eventType: TrackingEventType.PAGE_ENGAGEMENT,
			pageUrl: window.location.href,
			duration: timeSpent,
			metadata: {
				contentId: (route.name as string) || route.path,
				scrollDepth: maxScrollDepth.value,
			},
		});
	};

	// Track page view
	const trackPageView = (pageName?: string, additionalData?: Record<string, unknown>) => {
		pageStartTime.value = Date.now();
		scrollDepth.value = 0;
		interactionCount.value = 0;
		maxScrollDepth.value = 0;

		// Track initial page view
		tracking.trackEngagementEvent({
			eventType: TrackingEventType.PAGE_VIEW,
			pageUrl: window.location.href,
			duration: 0,
			metadata: {
				contentId: pageName || (route.name as string) || route.path,
				scrollDepth: 0,
				...additionalData,
			},
		});
	};

	onMounted(() => {
		trackPageView();

		// Add event listeners
		window.addEventListener('scroll', handleScroll, { passive: true });
		document.addEventListener('click', trackInteraction);
		document.addEventListener('keydown', trackInteraction);
		document.addEventListener('touchstart', trackInteraction);

		// Send engagement data every 30 seconds
		engagementTimer = window.setInterval(sendEngagementData, 30000);
	});

	onUnmounted(() => {
		// Send final engagement data
		sendEngagementData();

		// Clean up
		if (scrollTimer) clearTimeout(scrollTimer);
		if (engagementTimer) clearInterval(engagementTimer);

		window.removeEventListener('scroll', handleScroll);
		document.removeEventListener('click', trackInteraction);
		document.removeEventListener('keydown', trackInteraction);
		document.removeEventListener('touchstart', trackInteraction);
	});

	// Watch for route changes
	watch(
		() => route.path,
		() => {
			sendEngagementData();
			trackPageView();
		},
	);

	return {
		scrollDepth: scrollDepth.value,
		interactionCount: interactionCount.value,
		timeSpent: () => Date.now() - pageStartTime.value,
		trackPageView,
	};
}

// Form tracking composable
export function useFormTracking(formName: string) {
	const tracking = useTracking();

	const trackFieldInteraction = (
		fieldName: string,
		interactionType: 'focus' | 'blur' | 'change' | 'error',
		value?: string,
	) => {
		tracking.trackEngagementEvent({
			eventType: TrackingEventType.NAVIGATION_CLICK,
			pageUrl: window.location.href,
			metadata: {
				formName,
				fieldName,
				interactionType,
				value,
			},
		});
	};

	const trackFormError = (fieldName: string, errorMessage: string) => {
		tracking.trackPerformanceEvent({
			eventType: TrackingEventType.ERROR_OCCURRED,
			errorMessage,
			metadata: {
				errorType: 'validation',
				pageName: formName,
				userAction: `field_validation_${fieldName}`,
				fieldName,
			},
		});
	};

	return {
		trackFieldInteraction,
		trackFormError,
	};
}

// Question tracking for wizard
export function useQuestionTracking() {
	const tracking = useTracking();
	const questionStartTimes = ref<Record<string, number>>({});

	const startQuestion = (questionId: string) => {
		questionStartTimes.value[questionId] = Date.now();
	};

	const answerQuestion = (
		questionId: string,
		section: string,
		answerValue: number,
		changedAnswer = false,
	) => {
		const startTime = questionStartTimes.value[questionId];
		const responseTime = startTime ? Date.now() - startTime : 0;

		tracking.trackEngagementEvent({
			eventType: TrackingEventType.NAVIGATION_CLICK,
			pageUrl: window.location.href,
			metadata: {
				contentId: questionId,
				contentType: section,
				answerValue,
				changedAnswer,
				responseTime,
			},
		});
	};

	return {
		startQuestion,
		answerQuestion,
	};
}

// CTA tracking helper
export function useCTATracking() {
	const tracking = useTracking();

	const trackCTA = (
		ctaText: string,
		ctaType: 'primary' | 'secondary' | 'tertiary' = 'primary',
		ctaLocation: string = 'unknown',
		pageSection: string = 'unknown',
	) => {
		tracking.trackCTAClick({
			eventType: TrackingEventType.CTA_CLICKED,
			ctaType,
			ctaText,
			ctaLocation,
			metadata: {
				pageSection,
			},
		});
	};

	return {
		trackCTA,
	};
}

// Conversion funnel tracking
export function useConversionTracking() {
	const tracking = useTracking();

	const trackFunnelStep = (stepName: string, stepData?: Record<string, unknown>) => {
		switch (stepName) {
			case 'landing_viewed':
				tracking.trackEngagementEvent({
					eventType: TrackingEventType.PAGE_VIEW,
					pageUrl: window.location.href,
					metadata: {
						contentId: 'landing',
					},
				});
				break;

			case 'onboarding_started':
				tracking.trackOnboardingEvent({
					eventType: TrackingEventType.ONBOARDING_STEP_STARTED,
					stepNumber: 1,
					stepName: 'child_intro',
				});
				break;

			case 'onboarding_completed':
				tracking.trackOnboardingEvent({
					eventType: TrackingEventType.ONBOARDING_COMPLETED,
					metadata: {
						totalTime: stepData?.totalTime,
						childAge: stepData?.childAge,
						selectedChallenges: stepData?.challenges,
						selectedGoals: stepData?.goals,
						parentFeelings: stepData?.parentFeelings,
					},
				});
				break;

			case 'wizard_phase1_started':
				tracking.trackWizardEvent({
					eventType: TrackingEventType.WIZARD_PHASE1_STARTED,
					phase: 1,
				});
				break;

			case 'wizard_phase1_completed':
				tracking.trackWizardEvent({
					eventType: TrackingEventType.WIZARD_PHASE1_COMPLETED,
					phase: 1,
					metadata: {
						completionTime: stepData?.completionTime,
						questionsAnswered: stepData?.questionsAnswered,
						questionsSkipped: stepData?.questionsSkipped,
						averageResponseTime: stepData?.averageResponseTime,
					},
				});
				break;

			case 'wizard_phase2_started':
				tracking.trackWizardEvent({
					eventType: TrackingEventType.WIZARD_PHASE2_STARTED,
					phase: 2,
					metadata: {
						phase1Time: stepData?.phase1Time,
						breakDuration: stepData?.breakDuration,
					},
				});
				break;

			case 'wizard_phase2_completed':
				tracking.trackWizardEvent({
					eventType: TrackingEventType.WIZARD_PHASE2_COMPLETED,
					phase: 2,
					metadata: {
						totalTestTime: stepData?.totalTime,
						phase1Time: stepData?.phase1Time,
						phase2Time: stepData?.phase2Time,
					},
				});
				break;

			case 'results_viewed':
				tracking.trackEngagementEvent({
					eventType: TrackingEventType.PAGE_VIEW,
					pageUrl: window.location.href,
					metadata: {
						eventName: 'results_viewed',
						resultType: stepData?.resultType || 'basic',
						testCompletionTime: stepData?.testCompletionTime,
						learningPreferences: stepData?.learningPreferences,
					},
				});
				break;

			case 'upgrade_prompt_shown':
				tracking.trackEngagementEvent({
					eventType: TrackingEventType.PAGE_ENGAGEMENT,
					pageUrl: window.location.href,
					metadata: {
						eventName: 'upgrade_prompt_shown',
						promptLocation: stepData?.promptLocation,
						basicResultsViewTime: stepData?.basicResultsViewTime,
					},
				});
				break;

			case 'break_page_shown':
				tracking.trackEngagementEvent({
					eventType: TrackingEventType.PAGE_VIEW,
					pageUrl: window.location.href,
					metadata: {
						eventName: 'break_page_shown',
						contentId: 'learning_break',
						experimentId: stepData?.experimentId as string | undefined,
						variantId: stepData?.variantId as string | undefined,
					},
				});
				break;

			case 'break_page_skipped':
				tracking.trackEngagementEvent({
					eventType: TrackingEventType.PAGE_ENGAGEMENT,
					pageUrl: window.location.href,
					metadata: {
						eventName: 'break_page_skipped',
						variantId: (stepData?.variantId as string | undefined) ?? 'control',
					},
				});
				break;

			case 'payment_initiated':
				tracking.trackMonetizationEvent({
					eventType: TrackingEventType.PAYMENT_INITIATED,
					amount: stepData?.price as number | undefined,
					metadata: {
						analysisType: stepData?.analysisType as string | undefined,
						emailProvided: stepData?.email as string | undefined,
					},
				});
				break;

			case 'payment_completed':
				tracking.trackMonetizationEvent({
					eventType: TrackingEventType.PAYMENT_COMPLETED,
					amount: stepData?.amount as number | undefined,
					paymentIntentId: stepData?.paymentId as string | undefined,
					metadata: {
						emailProvided: stepData?.email,
						conversionTime: stepData?.conversionTime,
					},
				});
				break;
		}
	};

	return {
		trackFunnelStep,
	};
}
